'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { AuthService } from '@/hotdesk-ui/services/auth.service';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import PlansPopup, { PlansPopupPlan } from '@/hotdesk-ui/components/my-account-v4/PlansPopup';
import { VerifyPhoneModalV4 } from '@/hotdesk-ui/components/auth-v4';
import EditPhoneModal from '@/hotdesk-ui/components/my-account-v4/EditPhoneModal';
import { getActiveUserSubscriptions } from '@/hotdesk-ui/utils/subscriptionUtils';
import { buildCheckoutUpgradeUrl, consumeCheckoutUpgradeOpenPlansFlag, loadCheckoutUpgradeEligibility, peekCheckoutUpgradeOpenPlansFlag, saveCheckoutUpgradeOpenPlansFlag, type CheckoutUpgradeCountryConfig } from '@/hotdesk-ui/utils/checkoutUpgrade';
import styles from './MembershipOfferSection.module.scss';
export interface MembershipOfferSectionProps {
  listingDetails: MyListing | null;
  languageCode: string;
  /**
   * Optional: also hide when this listing already has an applicable pass.
   * Banner is always hidden if the user has any active membership.
   */
  hasActiveSubscription?: boolean;
}
const MembershipOfferSection: FC<MembershipOfferSectionProps> = ({
  listingDetails,
  languageCode,
  hasActiveSubscription = false
}) => {
  const t = useTranslations();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const token = useAppSelector((state: RootState) => state.auth.token);
  const isAuthModalOpen = useAppSelector((state: RootState) => state.auth.isOpenAuthModal);
  const popService = useMemo(() => new AuthPopupService(), []);
  const hasAnyActiveMembership = getActiveUserSubscriptions(user).length > 0;
  const [offerEligible, setOfferEligible] = useState(false);
  const [countryConfig, setCountryConfig] = useState<CheckoutUpgradeCountryConfig | null>(null);
  const [showPlans, setShowPlans] = useState(false);
  const [authGateActive, setAuthGateActive] = useState(false);
  const [sawAuthModal, setSawAuthModal] = useState(false);
  const [verifyShow, setVerifyShow] = useState(false);
  const [verifyPhoneNumber, setVerifyPhoneNumber] = useState('');
  const [editPhoneShow, setEditPhoneShow] = useState(false);
  const [pendingCheckoutUrl, setPendingCheckoutUrl] = useState<string | null>(null);
  const listingCountryName = listingDetails?.city?.country?.name ?? null;
  const listingId = listingDetails?.id ?? null;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await loadCheckoutUpgradeEligibility({
        languageCode,
        listingId,
        countryName: listingCountryName
      });
      if (cancelled) return;
      setOfferEligible(result.eligible);
      setCountryConfig(result.countryConfig);
    })();
    return () => {
      cancelled = true;
    };
  }, [languageCode, listingId, listingCountryName]);
  useEffect(() => {
    if (isAuthModalOpen) setSawAuthModal(true);
  }, [isAuthModalOpen]);
  const prevUserIdRef = useRef(user?.id);
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    prevUserIdRef.current = user?.id;
    if (prevUserId && !user?.id) {
      setShowPlans(false);
      setVerifyShow(false);
      setEditPhoneShow(false);
      setPendingCheckoutUrl(null);
      setAuthGateActive(false);
      setSawAuthModal(false);
      consumeCheckoutUpgradeOpenPlansFlag();
    }
  }, [user?.id]);
  useEffect(() => {
    const hasPendingFlag = peekCheckoutUpgradeOpenPlansFlag();
    if (!authGateActive && !hasPendingFlag) return;
    if (isAuthModalOpen) return;
    if (user?.id) {
      consumeCheckoutUpgradeOpenPlansFlag();
      setAuthGateActive(false);
      setSawAuthModal(false);
      if (getActiveUserSubscriptions(user).length === 0 && !hasActiveSubscription) {
        setShowPlans(true);
      }
      return;
    }
    if (authGateActive && sawAuthModal) {
      consumeCheckoutUpgradeOpenPlansFlag();
      setAuthGateActive(false);
      setSawAuthModal(false);
    }
  }, [user, isAuthModalOpen, authGateActive, sawAuthModal, hasActiveSubscription]);
  const showBanner = offerEligible && !!countryConfig && countryConfig.packages.length > 0 && !hasAnyActiveMembership && !hasActiveSubscription;
  const goToCheckout = (checkoutUrl: string) => {
    if (typeof window === 'undefined') return;
    window.location.href = checkoutUrl;
  };
  const getUserById = async (id: number) => {
    const apiConfig = await new ApiConfigration().getApiConfig(languageCode, token ?? '');
    const authService = new AuthService(apiConfig);
    return authService.getUserById(id);
  };
  const sendPhoneVerificationCode = async (phoneNumber: string, username?: string) => {
    const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
    const authService = new AuthService(apiConfig);
    await authService.sendPhoneCode({
      username: username ?? '',
      phoneNumber: phoneNumber.replace(/\s+/g, '')
    });
  };
  const handleBannerClick = async () => {
    if (!user?.id) {
      saveCheckoutUpgradeOpenPlansFlag();
      setAuthGateActive(true);
      setSawAuthModal(false);
      await popService.getPopup();
      return;
    }
    setShowPlans(true);
  };
  const handleSelectPlan = async (plan: PlansPopupPlan) => {
    const checkoutUrl = buildCheckoutUpgradeUrl(plan.subscription_package_id, plan.name);
    setShowPlans(false);
    if (!user?.id) {
      saveCheckoutUpgradeOpenPlansFlag();
      localStorage.setItem('redirectto', checkoutUrl);
      await popService.getPopup();
      return;
    }
    const refreshed = await getUserById(user.id ?? 0);
    if (refreshed?.isPhoneVerified) {
      goToCheckout(checkoutUrl);
      return;
    }
    if (refreshed?.phoneNumber) {
      setVerifyPhoneNumber(refreshed.phoneNumber);
      setPendingCheckoutUrl(checkoutUrl);
      setVerifyShow(true);
      try {
        await sendPhoneVerificationCode(refreshed.phoneNumber, refreshed.username);
      } catch (err) {
        console.error('Error sending phone verification code', err);
        toast.error(t('subscriptions.verifyPhoneToContinue'), {
          position: 'bottom-center'
        });
      }
      return;
    }
    setPendingCheckoutUrl(checkoutUrl);
    setEditPhoneShow(true);
  };
  if (!showBanner) return null;
  return <>
            <button type="button" className={styles.banner} onClick={handleBannerClick}>
                <span className={styles.main}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/assets/icons/smile-morphy.svg" alt="" aria-hidden="true" className={styles.icon} />
                    <span className={styles.copy}>
                        <span className={styles.title}>
                            {t('workspace-details.membershipOffer.sectionTitle')}
                        </span>
                        <span className={styles.subtitle}>
                            {t('workspace-details.membershipOffer.sectionSubtitle')}
                        </span>
                    </span>
                </span>
                <span className={styles.pills}>
                    <span className={styles.pill}>
                        {t('workspace-details.membershipOffer.discountedRates')}
                    </span>
                    <span className={styles.pill}>
                        {t('workspace-details.membershipOffer.flexiblePlans')}
                    </span>
                </span>
            </button>

            <PlansPopup show={showPlans} ctaLabel={t('subscriptions.subscribeNow')} packageIds={countryConfig?.packages ?? null} onClose={() => setShowPlans(false)} onSelectPlan={handleSelectPlan} data-sentry-element="PlansPopup" data-sentry-source-file="MembershipOfferSection.tsx" />

            <VerifyPhoneModalV4 show={verifyShow} handleClose={() => setVerifyShow(false)} language={languageCode} userId={user?.id} phoneNumber={verifyPhoneNumber} onVerified={() => {
      if (pendingCheckoutUrl) {
        goToCheckout(pendingCheckoutUrl);
      }
    }} data-sentry-element="VerifyPhoneModalV4" data-sentry-source-file="MembershipOfferSection.tsx" />
            {!!user && <EditPhoneModal show={editPhoneShow} onClose={() => setEditPhoneShow(false)} user={user} language={languageCode} onVerified={() => {
      if (pendingCheckoutUrl) {
        goToCheckout(pendingCheckoutUrl);
      }
    }} />}
        </>;
};
export default MembershipOfferSection;